import { useId, useRef, useState } from 'react';
import { ApiError, uploadFile } from '../api.ts';

/** What a finished upload leaves behind — enough to submit and to preview. */
export interface AttachedFile {
  id: string;
  name: string;
  mime: string;
}

/**
 * Pick a file, and it uploads straight away — so by the time the form is
 * submitted there is only an id to send, and a slow connection stalls the
 * photo rather than the whole "done" tap.
 */
export default function AttachField({
  label,
  accept,
  value,
  onChange,
  hint,
}: {
  label: string;
  accept: string;
  value: AttachedFile | null;
  onChange: (next: AttachedFile | null) => void;
  hint?: string;
}): React.ReactElement {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { file: saved } = await uploadFile(file);
      onChange({ id: saved.id, name: saved.name, mime: file.type });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That upload did not go through.');
    } finally {
      setUploading(false);
      // Clear the input so picking the same file again still fires onChange.
      if (input.current) input.current.value = '';
    }
  };

  if (value) {
    return (
      <div className="row" style={{ gap: 10, alignItems: 'center' }}>
        {value.mime.startsWith('image/') && (
          <img
            src={`/api/files/${value.id}`}
            alt={value.name}
            style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10 }}
          />
        )}
        <span className="small soft" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{value.name}</span>
        <button type="button" className="btn ghost" onClick={() => onChange(null)}>
          Remove
        </button>
      </div>
    );
  }

  return (
    <div className="field">
      <label htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        ref={input}
        type="file"
        accept={accept}
        disabled={uploading}
        onChange={(e) => void pick(e.target.files?.[0])}
      />
      {uploading ? <span className="hint">Uploading…</span> : hint && <span className="hint">{hint}</span>}
      {error && <span className="error">{error}</span>}
    </div>
  );
}
